import { apiService } from '@/feature/api/axios';
import { handleApiError } from '@/shared/utils/errorUtils';

import type {
  ITokenUsageBreakdownResponse,
  ITokenUsageOrgDetailsResponse,
  ITokenUsageQuery,
  ITokenUsageSummaryResponse,
  TokenUsageGroupBy,
} from '../types/tokenUsage';

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/** API expects `from_date` / `to_date` as `YYYY-MM-DD`, not unix timestamps. */
function toApiDateParam(value: number | string | Date): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const parsed = new Date(trimmed);
    if (Number.isFinite(parsed.getTime())) {
      return formatYyyyMmDd(parsed);
    }
    return trimmed;
  }
  if (value instanceof Date) {
    return formatYyyyMmDd(value);
  }
  const ms = value > 10_000_000_000 ? value : value * 1000;
  return formatYyyyMmDd(new Date(ms));
}

function formatYyyyMmDd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const tokenUsageService = {
  /**
   * GET `/api/ai-automation-admin/token-usage/`
   * Returns summary + organizations list.
   */
  getTokenUsageSummary: async (
    query: ITokenUsageQuery
  ): Promise<ITokenUsageSummaryResponse | null> => {
    try {
      const api = apiService();
      const params: Record<string, unknown> = {};
      if (isFiniteNumber(query.fromDate)) {
        params.from_date = toApiDateParam(query.fromDate);
      }
      if (isFiniteNumber(query.toDate)) {
        params.to_date = toApiDateParam(query.toDate);
      }
      if (typeof query.onlyDefault === 'boolean') {
        params.only_default = query.onlyDefault;
      }

      const response = await api.get<ITokenUsageSummaryResponse>(
        '/api/ai-automation-admin/token-usage/',
        params
      );
      return response;
    } catch (error) {
      handleApiError(error, 'Failed to fetch token usage. Please try again.');
      return null;
    }
  },

  /**
   * GET `/api/ai-automation-admin/token-usage/<org_id>/`
   * Returns breakdown results + total for a single org.
   */
  getTokenUsageBreakdown: async (args: {
    orgId: string;
    groupBy?: TokenUsageGroupBy[];
    fromDate: number;
    toDate: number;
    onlyDefault?: boolean;
  }): Promise<ITokenUsageBreakdownResponse | null> => {
    try {
      const api = apiService();
      const params: Record<string, unknown> = {
        from_date: toApiDateParam(args.fromDate),
        to_date: toApiDateParam(args.toDate),
      };
      if (args.groupBy && args.groupBy.length > 0) {
        params.group_by = args.groupBy.join(',');
      }
      if (typeof args.onlyDefault === 'boolean') {
        params.only_default = args.onlyDefault;
      }

      const response = await api.get<ITokenUsageBreakdownResponse>(
        `/api/ai-automation-admin/token-usage/${args.orgId}/`,
        params
      );
      return response;
    } catch (error) {
      handleApiError(error, 'Failed to fetch token usage breakdown. Please try again.');
      return null;
    }
  },

  /**
   * GET `/api/ai-automation-admin/token-usage/<org_id>/`
   * Returns totals and multiple breakdown arrays (by_user/by_provider/by_project/by_day/by_used_for).
   */
  getTokenUsageOrgDetails: async (args: {
    orgId: string;
    fromDate: number;
    toDate: number;
    onlyDefault?: boolean;
  }): Promise<ITokenUsageOrgDetailsResponse | null> => {
    try {
      const api = apiService();
      const params: Record<string, unknown> = {
        from_date: toApiDateParam(args.fromDate),
        to_date: toApiDateParam(args.toDate),
      };
      if (typeof args.onlyDefault === 'boolean') {
        params.only_default = args.onlyDefault;
      }

      const response = await api.get<ITokenUsageOrgDetailsResponse>(
        `/api/ai-automation-admin/token-usage/${args.orgId}/`,
        params
      );
      return response;
    } catch (error) {
      handleApiError(error, 'Failed to fetch token usage details. Please try again.');
      return null;
    }
  },
};
